/**
 * 寄養服務申請記錄
 */
import React, { useEffect, useState } from 'react';
import { useIntl, useSelector } from 'umi';
import type { UserState } from 'umi';
import classPrefix from 'prefix-classnames';
import { getWebFosterBreadcrumbItems } from '@/utils/Breadcrumb';
import Breadcrumb from '@/components/Breadcrumb';
import './css/WebApplyRecordService.less';
import { Button, Card, Modal, Space } from 'antd';
import { formatDateTime2, formatDateTime } from '@/utils/dateutils';
import { usePersistFn } from 'ahooks';
import { getModalConfig } from '@/components/Modal/useModalMethod';
import { commonPostRequest, removeToken } from '@/utils/apiUtils';
import { buildSignature } from '@/utils/signUtis';

const px = classPrefix('web-foster-apply-list', '-') as (param: string) => string;

// 定义数据类型
interface ApplyItem {
  id: string;
  appNo: string;
  applicantName: string;
  spouseName: string;
  submitTime: string;
  applicantCellphone: string;
  applicantAreano: string;
  applicantAddress: string;
  flowStatus: number;
  cancelStatus: number;
}

// 加載數據
// 獲取申請記錄數據
const fetchApplyList = async (params?: Record<string, unknown>): Promise<ApplyItem[]> => {
  return commonPostRequest<ApplyItem[]>('/api/adoptappli/applylist', params);
};

// 添加取消申请的API调用函数
const cancelApply = async (params?: Record<string, unknown>): Promise<unknown> => {
  return commonPostRequest<unknown>('/api/adoptappli/cancelApply', params);
};


export default function WebNurseryOpendayList(props: unknown) {
  const intl = useIntl();
  const [applyList, setApplyList] = useState<ApplyItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAdd, setIsAdd] = useState(true);
  // 获取用户信息
  const { profile } = useSelector(({ user }: { user: UserState }) => user);

  const getStatusText = (flowStatus: number, cancelStatus?: number): string => {
    // 如果取消状态为1，直接显示已取消
    if (cancelStatus === 1) {
      return intl.formatMessage({ id: '已取消' });
    }
    switch (flowStatus) {
      case 0:
        return intl.formatMessage({ id: '草稿' });
      case 1:
        return intl.formatMessage({ id: '待評估' });
      case 2:
        return intl.formatMessage({ id: '評估完成' });
      default:
        return intl.formatMessage({ id: '未知狀態' });
    }
  };

  // 申请按钮点击处理
  const closePage = usePersistFn(() => {
    // 如果未勾选，则弹出提示
    Modal.confirm({
      ...getModalConfig(intl),
      icon: null,
      title: (
        <span style={{ fontWeight: 'bold', color: '#13a07b' }}>
          {intl.formatMessage({ id: '温馨提示' })}
        </span>
      ),
      content: intl.formatMessage({ id: '確認離開當前頁面?' }),
      onOk() {
        removeToken();
        window.top.close();
      },
    });
  });


  // 在组件内部添加 handleCancelApply 方法
  const handleCancelApply = usePersistFn((applicationId: string) => {
    Modal.confirm({
      ...getModalConfig(intl),
      icon: null,
      title: (
        <span style={{ fontWeight: 'bold', color: '#13a07b' }}>
          {intl.formatMessage({ id: '温馨提示' })}
        </span>
      ),
      okText: intl.formatMessage({ id: '確認' }),
      content: intl.formatMessage({ id: '確認取消申請嗎？' }),
      onOk() {
        const params = {
          id: applicationId,
        };
        return cancelApply(params)
          .then((data) => {
            if (data?.success) {
              // 取消成功后刷新页面
              window.location.reload();
            } else {
              Modal.error({
                title: intl.formatMessage({ id: '提示' }),
                content: data?.msg || intl.formatMessage({ id: '操作失败' }),
              });
            }
          })
          .catch((error) => {
            console.error('取消申请失败:', error);
            Modal.error({
              title: intl.formatMessage({ id: '提示' }),
              content: intl.formatMessage({ id: '操作失败' }),
            });
          });
      },
    });
  });

  useEffect(() => {
    if (!profile || !profile.euid) {
      setLoading(true);
      return undefined;
    }

    let isMounted = true;

    const eeuid = profile.euid;
    const params = {
      euid: eeuid,
    };

    setLoading(true);
    setTimeout(() => {
      fetchApplyList(params)
        .then((data) => {
          if (!isMounted) return;
          if (data != null) {
            if (data.length === 0) {
              window.location.href = '/foster-service?platform=personal';
            }
            setApplyList(data);
            const hasStatusOne = data.some(item => (item.flowStatus === 1 && item.cancelStatus === 0));
            if (hasStatusOne) {
              setIsAdd(false);
            }
          } else {
            window.location.href = '/foster-service?platform=personal';
          }
        })
        .catch((error) => {
          if (!isMounted) return;
          console.error('获取列表失败:', error);
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false);
          }
        });
    }, 1000);

    return () => {
      isMounted = false;
    };
  },  [profile?.euid]);


  useEffect(() => {
    if (!loading && applyList.length === 0) {
      window.location.href = '/foster-service?platform=personal';
    }
  }, [loading, applyList.length]);


  // 注釋
  return (
    <>
      <div className={px('root')}>
        {/* 面包屑 */}
        <div className={px('breadcrumb')}>
          <Breadcrumb itemList={getWebFosterBreadcrumbItems()} />
        </div>
        <div className={px('title')}>{intl.formatMessage({ id: '申請成為寄養父母' })}</div>
        <div className={px('container')}>
          <Space direction="vertical" size={24} style={{ width: '100%' }}>
            {applyList.length > 0 ? (
              applyList.map(item => (
                <Card
                  key={item.id}
                  title={(
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{`${intl.formatMessage({ id: '申請編號:' })} ${item.appNo}`}</span>
                      {/* eslint-disable-next-line max-len */}
                      <span style={{
                        fontSize: '14px',
                        lineHeight: '30px',
                        // eslint-disable-next-line max-len
                        color: getStatusText(item.flowStatus, item.cancelStatus) === intl.formatMessage({ id: '評估完成' }) ? '#1aa17d' : '#fec207',
                        border: `1px solid ${getStatusText(item.flowStatus, item.cancelStatus) === intl.formatMessage({ id: '評估完成' }) ? '#1aa17d' : '#fec207'}`,
                        padding: '0px 15px',
                        background: '#fff',
                        borderRadius: '15px',
                      }}
                      >
                        {getStatusText(item.flowStatus, item.cancelStatus)}
                      </span>
                    </div>
                  )}
                  style={{ background: '#d8ebe7' }}
                  bodyStyle={{ background: '#fff' }}
                >
                  <div>
                    <p>
                      <span className={px('spanLeft')}>{intl.formatMessage({ id: '申請人姓名' })}</span>
                      <span className={px('spanRight')}>{item.applicantName}</span>
                    </p>
                    <p>
                      <span className={px('spanLeft')}>{intl.formatMessage({ id: '配偶姓名' })}</span>
                      <span className={px('spanRight')}>{item.spouseName}</span>
                    </p>
                    <p>
                      <span className={px('spanLeft')}>{intl.formatMessage({ id: '申請時間' })}</span>
                      <span className={px('spanRight')}>
                        {item.submitTime ? formatDateTime2(item.submitTime) : item.submitTime}
                      </span>
                    </p>
                    <p>
                      <span className={px('spanLeft')}>{intl.formatMessage({ id: '聯繫電話' })}</span>
                      <span className={px('spanRight')}>{item.applicantAreano}-{item.applicantCellphone}</span>
                    </p>
                    <p>
                      <span className={px('spanLeft')}>{intl.formatMessage({ id: '地址' })}</span>
                      <span className={px('spanRight')}>{item.applicantAddress}</span>
                    </p>
                    <p className={px('btnP')}>
                      {(item.cancelStatus !== 1 && item.flowStatus === 1) && (
                        <Button
                          type="default"
                          className={px('signBtn')}
                          onClick={() => {
                            handleCancelApply(item.id);
                          }}
                        >
                          {intl.formatMessage({ id: '取消申請' })}
                        </Button>
                      )}
                      <Button
                        type="primary"
                        className={px('viewBtn')}
                        onClick={() => {
                          window.location.href = `/foster-apply-detail?id=${item.id}&platform=personal`;
                        }}
                      >
                        {intl.formatMessage({ id: '查看詳情' })}
                      </Button>
                    </p>
                  </div>
                </Card>
              ))
            ) : null}
          </Space>
        </div>
      </div>
      {!loading && applyList.length > 0 ? (
        <div className={px('-footer-button')}>
          <button
            type="button"
            className="ant-btn ant-btn-default"
            onClick={() => {
              closePage();
            }}
          >
            <span style={{ color: '#13a07b' }}>{intl.formatMessage({ id: '關閉' })}</span>
          </button>
          <button
            type="button"
            className="ant-btn ant-btn-primary"
            style={{ width: '160px' }}
            onClick={() => {
              /* handleApply(); */
              if (isAdd) {
                window.location.href = '/foster-service?platform=personal';
              } else {
                Modal.error({
                  icon: null,
                  okText: intl.formatMessage({ id: '知道了' }),
                  content: (
                    <div>
                      <p style={{ fontWeight: '600', color: '#13a07b', fontSize: '16px', marginTop: '-15px', marginBottom: '10px' }}>
                        {intl.formatMessage({ id: '溫馨提示' })}
                      </p>
                      <p>{intl.formatMessage({ id: '在評估未完成之前，不可新增申請。' })}</p>
                    </div>
                  ),
                });
              }
            }}
          >
            <span>{intl.formatMessage({ id: '新申請' })}</span>
          </button>
        </div>
      ) : null}
    </>
  );
}
